import React, { useState, useRef, useEffect } from 'react';
import { AlertTriangle, Lock, Eye, EyeOff, KeyRound, Info } from 'lucide-react';
import TailwindModal, { TailwindModalHeader, TailwindModalBody, TailwindModalFooter } from './TailwindModal';

const DisableTwoFactorModal = ({
    isOpen,
    onClose,
    onSubmit,
    loading,
    error
}) => {
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [recoveryCode, setRecoveryCode] = useState('');
    const [useRecovery, setUseRecovery] = useState(false);
    const [localError, setLocalError] = useState('');
    const inputRefs = useRef([]);

    // Keep error in sync
    useEffect(() => {
        setLocalError(error);
    }, [error]);

    useEffect(() => {
        if (isOpen) {
            setPassword('');
            setCode(['', '', '', '', '', '']);
            setRecoveryCode('');
            setUseRecovery(false);
            setLocalError('');
            setShowPassword(false);
        }
    }, [isOpen]);

    const handleCodeChange = (index, value) => {
        const val = value.replace(/\D/g, "").slice(-1);
        if (value && !val) return;
        const newCode = [...code];
        newCode[index] = val;
        setCode(newCode);

        if (val && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
        setLocalError('');
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            e.preventDefault();
            const newCode = [...code];
            newCode[index - 1] = '';
            setCode(newCode);
            setLocalError('');
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < 5) {
            e.preventDefault();
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
        if (pastedData) {
            const newCode = [...code];
            for (let i = 0; i < pastedData.length; i++) {
                newCode[i] = pastedData[i];
            }
            setCode(newCode);
            const nextIndex = Math.min(pastedData.length, 5);
            inputRefs.current[nextIndex]?.focus();
        }
    };

    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        const finalCode = useRecovery ? recoveryCode.replace(/\s/g, "") : code.join('');
        
        if (!password) {
            setLocalError('Please enter your password');
            return;
        }

        if (!useRecovery && finalCode.length !== 6) {
            setLocalError('Please enter a 6-digit code');
            return;
        }
        if (useRecovery && finalCode.length !== 8) {
            setLocalError('Recovery code must be 8 characters');
            return;
        }

        setLocalError('');
        onSubmit(finalCode, useRecovery, password);
    };

    return (
        <TailwindModal isOpen={isOpen} toggle={onClose} maxWidth="445px">
            <TailwindModalHeader title="Disable Two-Factor Authentication" toggle={onClose} />
            <TailwindModalBody>
                <div className="sm:px-2 pb-1">
                    {/* Warning Box */}
                    <div className="bg-[#FFF8F0] border border-[#FFE8D6] rounded-lg px-3.5 py-3 flex gap-3 mb-4">
                        <AlertTriangle className="text-orange-500 shrink-0 mt-0.5" size={20} fill="#F97316" color="#FFF8F0" />
                        <div>
                            <p className="text-[13px] font-bold text-[#9A5016] leading-snug mb-0.5">
                                This will remove an important layer of security from your account.
                            </p>
                            <p className="text-xs text-[#A86A36]">
                                You can enable 2FA again anytime.
                            </p>
                        </div>
                    </div>

                    <form id="disable-2fa-form" onSubmit={handleSubmit}>
                        {/* Account Password */}
                        <div className="mb-3">
                            <label className="block text-[13px] font-bold text-slate-800 mb-1.5">
                                Account Password <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    value={password} 
                                    onChange={e => { setPassword(e.target.value); setLocalError(''); }} 
                                    className="w-full pl-9 pr-10 py-2 bg-white border border-slate-200 rounded-lg text-[13px] sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-slate-800 placeholder-slate-400 transition-colors shadow-sm" 
                                    placeholder="Enter your password" 
                                />
                                <button 
                                    type="button" 
                                    onClick={() => setShowPassword(!showPassword)} 
                                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>
                        </div>

                        {/* Authenticator Code / Recovery Code */}
                        <div className="mb-1">
                            <label className="block text-[13px] font-bold text-slate-800 mb-1">
                                {useRecovery ? "Recovery Code" : "Authenticator Code"} <span className="text-red-500">*</span>
                            </label>
                            <p className="text-[13px] text-slate-500 mb-2.5">
                                {useRecovery 
                                    ? "Enter one of your 8-character recovery codes."
                                    : "Enter the 6-digit code from your authenticator app."}
                            </p>

                            {!useRecovery ? (
                                <div className="flex justify-center gap-2 sm:gap-2.5 mb-4" onPaste={handlePaste}>
                                    {code.map((digit, index) => (
                                        <input
                                            key={index}
                                            ref={el => inputRefs.current[index] = el}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={2}
                                            value={digit}
                                            onFocus={(e) => e.target.select()}
                                            onChange={(e) => handleCodeChange(index, e.target.value)}
                                            onKeyDown={(e) => handleKeyDown(index, e)}
                                            className="w-[42px] h-[42px] sm:w-[46px] sm:h-[46px] text-center text-xl font-bold text-slate-800 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors shadow-sm placeholder-slate-400"
                                            placeholder="•"
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="mb-4">
                                    <input 
                                        type="text"
                                        maxLength={8}
                                        value={recoveryCode}
                                        onChange={(e) => { setRecoveryCode(e.target.value); setLocalError(''); }}
                                        placeholder="e.g. 1a2b3c4d"
                                        className="w-full px-4 py-2 bg-white border border-slate-200 rounded-lg text-center text-[13px] font-bold tracking-widest text-slate-800 uppercase focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm"
                                    />
                                </div>
                            )}

                            {/* Divider OR */}
                            <div className="relative flex items-center mb-4">
                                <div className="flex-grow border-t border-slate-100"></div>
                                <span className="flex-shrink-0 mx-4 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">OR</span>
                                <div className="flex-grow border-t border-slate-100"></div>
                            </div>

                            {/* Use Recovery / Authenticator Toggle */}
                            <div className="bg-[#F4F8FF] rounded-xl px-4 py-3 flex items-start sm:items-center gap-3.5 mb-1">
                                <div className="text-blue-600 shrink-0 mt-0.5 sm:mt-0">
                                    <KeyRound size={20} className="rotate-45" fill="#3B82F6" color="#F4F8FF" />
                                </div>
                                <div>
                                    <p className="text-[13px] text-slate-700 font-medium leading-snug">
                                        {useRecovery ? "Found your authenticator app?" : "Don't have access to your authenticator app?"}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setUseRecovery(!useRecovery);
                                            setLocalError('');
                                        }}
                                        className="text-[13px] font-bold text-blue-600 hover:text-blue-700 transition-colors text-left mt-0.5"
                                    >
                                        {useRecovery ? "Use an authenticator code instead" : "Use a recovery code instead"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {localError && (
                            <div className="mt-4 text-[13px] font-medium text-red-600 bg-red-50 px-3 py-2.5 rounded-lg border border-red-100 flex items-center gap-2">
                                <Info size={16} className="shrink-0" />
                                <span>{localError}</span>
                            </div>
                        )}
                    </form>
                </div>
            </TailwindModalBody>
            <TailwindModalFooter>
                <div className="flex w-full items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-50 transition-colors text-[13px] shadow-sm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="disable-2fa-form"
                        disabled={loading}
                        className="flex-1 flex items-center justify-center px-4 py-2 bg-[#E13B45] text-white rounded-lg font-bold hover:bg-red-600 transition-colors text-[13px] disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_2px_10px_rgba(225,59,69,0.3)]"
                    >
                        {loading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Disabling...
                            </>
                        ) : "Disable 2FA"}
                    </button>
                </div>
            </TailwindModalFooter>
        </TailwindModal>
    );
};

export default DisableTwoFactorModal;
